<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useUserStore } from '../stores/userStore'
import * as adminService from '../services/adminService'
import { formatDateTimeInTimezone } from '../utils/datetimeUtil'
import { formatIPAddress } from '../utils/ipUtil'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'close'): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const userStore = useUserStore()
const activeTab = ref<'settings' | 'users' | 'activity'>('settings')

// 設定標籤頁
const windowMinutes = ref(3)
const maxFailures = ref(5)
const lockoutMinutes = ref(30)
const settingsLoading = ref(false)
const settingsError = ref('')
const settingsSaved = ref(false)

// 使用者群組標籤頁
const usersList = ref<adminService.UserListItem[]>([])
const usersLoading = ref(false)
const usersError = ref('')
const updatingUserId = ref<number | null>(null)
const updateError = ref('')

// 活動記錄標籤頁
const searchText = ref('')
const activityLogs = ref<adminService.ActivityLog[]>([])
const totalLogs = ref(0)
const currentPage = ref(1)
const logsLoading = ref(false)
const logsError = ref('')

const loadSettings = async () => {
  if (!userStore.csrfToken) return

  settingsLoading.value = true
  settingsError.value = ''

  const result = await adminService.getAdminSettings()
  if (result.success && result.data) {
    windowMinutes.value = result.data.window_minutes
    maxFailures.value = result.data.max_failures
    lockoutMinutes.value = result.data.lockout_minutes
  } else {
    settingsError.value = result.error || '無法載入設定'
  }

  settingsLoading.value = false
}

const loadUsers = async () => {
  if (!userStore.csrfToken) return

  usersLoading.value = true
  usersError.value = ''

  const result = await adminService.getUsers()
  if (result.success && result.data) {
    usersList.value = result.data.users
  } else {
    usersError.value = result.error || '無法載入使用者列表'
  }

  usersLoading.value = false
}

const updateRole = async (userId: number, newRole: string) => {
  if (!userStore.csrfToken) return

  updatingUserId.value = userId
  updateError.value = ''

  const result = await adminService.updateUserRole( userId, newRole)

  if (result.success) {
    const user = usersList.value.find(u => u.id === userId)
    if (user) {
      user.role = newRole as 'sysadmin' | 'group_admin' | 'user'
    }
  } else {
    updateError.value = result.error || '無法更新角色'
    const user = usersList.value.find(u => u.id === userId)
    if (user) {
      // 無聲失敗，UI 保持原值（select 已自動還原）
    }
  }

  updatingUserId.value = null
}

const saveSettings = async () => {
  if (!userStore.csrfToken) return

  // 驗證
  if (windowMinutes.value < 1 || windowMinutes.value > 60) {
    settingsError.value = '登入嘗試時間窗口必須在 1-60 分鐘之間'
    return
  }
  if (maxFailures.value < 1 || maxFailures.value > 60) {
    settingsError.value = '最多登入失敗次數必須在 1-60 次之間'
    return
  }
  if (lockoutMinutes.value < 1 || lockoutMinutes.value > 1440) {
    settingsError.value = '鎖定時間必須在 1-1440 分鐘之間'
    return
  }

  settingsLoading.value = true
  settingsError.value = ''
  settingsSaved.value = false

  const result = await adminService.updateAdminSettings( {
    window_minutes: windowMinutes.value,
    max_failures: maxFailures.value,
    lockout_minutes: lockoutMinutes.value,
  })

  if (result.success) {
    settingsSaved.value = true
    setTimeout(() => {
      settingsSaved.value = false
    }, 3000)
  } else {
    settingsError.value = result.error || '無法保存設定'
  }

  settingsLoading.value = false
}

const loadActivityLogs = async () => {
  if (!userStore.csrfToken) return

  logsLoading.value = true
  logsError.value = ''

  const result = await adminService.getActivityLogs( {
    search: searchText.value,
    page: currentPage.value,
    limit: 50,
  })

  if (result.success && result.data) {
    activityLogs.value = result.data.logs
    totalLogs.value = result.data.total
  } else {
    logsError.value = result.error || '無法載入活動記錄'
  }

  logsLoading.value = false
}

const handleSearch = () => {
  currentPage.value = 1
  loadActivityLogs()
}

const exportCSV = async () => {
  if (!userStore.csrfToken) return

  try {
    const blob = await adminService.exportActivityLogs()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity_logs_${new Date().getTime()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    logsError.value = '無法匯出活動記錄'
  }
}

watch(
  () => [activeTab.value, userStore.csrfToken],
  async () => {
    if (!userStore.csrfToken) return

    if (activeTab.value === 'settings') {
      await loadSettings()
    } else if (activeTab.value === 'users') {
      await loadUsers()
    } else if (activeTab.value === 'activity') {
      await loadActivityLogs()
    }
  },
  { immediate: false }
)

onMounted(() => {
  if (userStore.csrfToken) {
    loadSettings()
  }
})

const formatDateTime = (utcDatetime: string): string => {
  return formatDateTimeInTimezone(utcDatetime, 'Asia/Taipei', 'YYYY-MM-DD HH:MM')
}
</script>

<template>
  <div v-if="isOpen" class="dialog-overlay">
    <div class="dialog-box">
      <div class="dialog-header">
        <h2>管理控制台</h2>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <!-- 標籤列 -->
      <div class="tab-list">
        <button
          :class="{ active: activeTab === 'settings' }"
          @click="activeTab = 'settings'"
        >
          使用者設定
        </button>
        <button
          :class="{ active: activeTab === 'users' }"
          @click="activeTab = 'users'"
        >
          使用者群組
        </button>
        <button
          :class="{ active: activeTab === 'activity' }"
          @click="activeTab = 'activity'"
        >
          使用者活動記錄
        </button>
      </div>

      <!-- Tab 1: 使用者設定 -->
      <div v-if="activeTab === 'settings'" class="tab-content">
        <div class="settings-section">
          <div class="setting-item">
            <label>登入嘗試限制</label>
            <div class="setting-inputs">
              <input
                v-model.number="windowMinutes"
                type="number"
                min="1"
                max="60"
                class="input-field"
              />
              <span>分鐘 登入失敗</span>
              <input
                v-model.number="maxFailures"
                type="number"
                min="1"
                max="60"
                class="input-field"
              />
              <span>次</span>
            </div>
          </div>

          <div class="setting-item">
            <label>帳號鎖定</label>
            <div class="setting-inputs">
              <span>鎖定時間：</span>
              <input
                v-model.number="lockoutMinutes"
                type="number"
                min="1"
                max="1440"
                class="input-field"
              />
              <span>分鐘</span>
            </div>
          </div>

          <div v-if="settingsError" class="error-message">{{ settingsError }}</div>
          <div v-if="settingsSaved" class="success-message">設定已保存</div>

          <div class="button-group">
            <button
              :disabled="settingsLoading"
              @click="saveSettings"
              class="btn btn-primary"
            >
              {{ settingsLoading ? '儲存中...' : '儲存' }}
            </button>
            <button
              @click="$emit('close')"
              class="btn btn-secondary"
            >
              取消
            </button>
          </div>
        </div>
      </div>

      <!-- Tab 2: 使用者群組 -->
      <div v-if="activeTab === 'users'" class="tab-content">
        <div class="users-section">
          <div v-if="usersError" class="error-message">{{ usersError }}</div>
          <div v-if="updateError" class="error-message">{{ updateError }}</div>

          <div class="users-table-container">
            <table class="users-table">
              <thead>
                <tr>
                  <th>帳號</th>
                  <th>暱稱</th>
                  <th>電子信箱</th>
                  <th>群組</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="usersLoading">
                  <td colspan="4" class="loading">載入中...</td>
                </tr>
                <tr v-else-if="usersList.length === 0">
                  <td colspan="4" class="empty">無使用者</td>
                </tr>
                <tr v-for="user in usersList" :key="user.id">
                  <td class="col-username">{{ user.username }}</td>
                  <td class="col-nickname">{{ user.nickname }}</td>
                  <td class="col-email">{{ user.email }}</td>
                  <td class="col-role">
                    <select
                      :value="user.role"
                      :disabled="user.username === 'patrick0913' || updatingUserId === user.id"
                      @change="e => updateRole(user.id, (e.target as HTMLSelectElement).value)"
                      class="role-select"
                    >
                      <option value="sysadmin">系統管理員</option>
                      <option value="group_admin" disabled>群組管理員（未開放）</option>
                      <option value="user">一般使用者</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Tab 3: 使用者活動記錄 -->
      <div v-if="activeTab === 'activity'" class="tab-content">
        <div class="activity-section">
          <div class="search-bar">
            <input
              v-model="searchText"
              type="text"
              placeholder="搜尋帳號"
              class="input-field search-input"
              @keyup.enter="handleSearch"
            />
            <button
              :disabled="logsLoading"
              @click="handleSearch"
              class="btn btn-secondary"
            >
              搜尋
            </button>
            <button
              @click="exportCSV"
              class="btn btn-secondary"
            >
              輸出CSV
            </button>
          </div>

          <div v-if="logsError" class="error-message">{{ logsError }}</div>

          <div class="logs-table-container">
            <table class="logs-table">
              <thead>
                <tr>
                  <th>編號</th>
                  <th>日期/時間</th>
                  <th>記錄</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="logsLoading">
                  <td colspan="3" class="loading">載入中...</td>
                </tr>
                <tr v-else-if="activityLogs.length === 0">
                  <td colspan="3" class="empty">無記錄</td>
                </tr>
                <tr v-for="log in activityLogs" :key="log.id">
                  <td class="col-id">{{ log.id }}</td>
                  <td class="col-time">{{ formatDateTime(log.created_at) }}</td>
                  <td class="col-message">{{ log.message }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination">
            總共 {{ totalLogs }} 筆記錄
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-box {
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.dialog-header h2 {
  margin: 0;
  font-size: 20px;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #111827;
}

.tab-list {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 20px;
}

.tab-list button {
  padding: 12px 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-list button.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.tab-list button:hover:not(.active) {
  color: #374151;
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.setting-item label {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.setting-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.input-field {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  width: 80px;
}

.input-field:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.activity-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.logs-table-container {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: auto;
  flex: 1;
  max-height: 400px;
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.logs-table thead {
  background: #f9fafb;
  position: sticky;
  top: 0;
}

.logs-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.logs-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
  word-break: break-word;
}

.logs-table tbody tr:hover {
  background: #f9fafb;
}

.col-id {
  width: 60px;
  text-align: center;
}

.col-time {
  width: 160px;
  white-space: nowrap;
}

.col-message {
  flex: 1;
}

.logs-table .loading,
.logs-table .empty {
  text-align: center;
  color: #9ca3af;
  padding: 20px 12px;
}

.pagination {
  font-size: 13px;
  color: #6b7280;
}

.users-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.users-table-container {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: auto;
  flex: 1;
  max-height: 400px;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.users-table thead {
  background: #f9fafb;
  position: sticky;
  top: 0;
}

.users-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.users-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
  word-break: break-word;
}

.users-table tbody tr:hover {
  background: #f9fafb;
}

.col-username {
  width: 120px;
  font-weight: 500;
}

.col-nickname {
  width: 120px;
}

.col-email {
  flex: 1;
  min-width: 150px;
}

.col-role {
  width: 160px;
  text-align: center;
}

.role-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  background: white;
  color: #1f2937;
}

.role-select:hover:not(:disabled) {
  border-color: #9ca3af;
  background: #f9fafb;
}

.role-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.role-select:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

.users-table .loading,
.users-table .empty {
  text-align: center;
  color: #9ca3af;
  padding: 20px 12px;
}

.error-message {
  padding: 10px 12px;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 4px;
  font-size: 13px;
}

.success-message {
  padding: 10px 12px;
  background: #dcfce7;
  color: #166534;
  border-radius: 4px;
  font-size: 13px;
}

.button-group {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e5e7eb;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background: #d1d5db;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
